import React, { useEffect, useState } from 'react';
import { storage } from '../../../services/StorageService';
import type { AccessibilityPrefs } from '../../../../shared/userprefs/schema';
import { SettingsToggle } from './components/SettingsToggle';

/**
 * AccessibilityTab — Settings → Accessibility. The player-facing control surface
 * for the accessibility UserPrefs slice, read by the motion seam
 * (systems/accessibility/motionPrefs) that gates the Pixi canvas.
 */
export const AccessibilityTab: React.FC = () => {
  const [a11y, setA11y] = useState<AccessibilityPrefs>(() => storage.loadUserPrefs().accessibility);

  useEffect(() => storage.subscribeUserPrefs((p) => setA11y(p.accessibility)), []);

  const patch = (next: Partial<AccessibilityPrefs>): void => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({ ...cur, accessibility: { ...cur.accessibility, ...next } });
  };

  return (
    <div>
      <section className="settings-section" aria-labelledby="settings-motion-heading">
        <h3 className="settings-section-title" id="settings-motion-heading">Motion & Comfort</h3>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Reduce motion</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Calms ambient scene motion and turns off camera shake. “Auto” follows your system setting.
              </div>
            </div>
            <select
              value={a11y.reduceMotion}
              onChange={(e) => patch({ reduceMotion: e.target.value as AccessibilityPrefs['reduceMotion'] })}
              data-testid="settings-reduce-motion"
              aria-label="Reduce motion"
            >
              <option value="auto">Auto (system)</option>
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Disable screen flashes</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Suppress full-screen combat flashes (photosensitivity).
              </div>
            </div>
            <SettingsToggle
              checked={a11y.disableScreenFlash}
              onChange={() => patch({ disableScreenFlash: !a11y.disableScreenFlash })}
              data-testid="settings-disable-screen-flash"
              aria-label="Disable screen flashes"
            />
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Screen shake</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Camera-shake intensity on impacts. 0 = off. (Forced off by Reduce motion.)
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(a11y.shakeIntensity * 100)}
                onChange={(e) => patch({ shakeIntensity: Number(e.target.value) / 100 })}
                data-testid="settings-shake-intensity"
                aria-label="Screen shake"
              />
              <output style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--tome-ochre, #8a5a19)',
                minWidth: 32,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {Math.round(a11y.shakeIntensity * 100)}%
              </output>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
